// src/components/admin/AdminQuickActionsWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  Grid,
  Typography,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Event as EventIcon,
  Backup as BackupIcon,
  Settings as SettingsIcon,
  Email as EmailIcon,
  Assessment as ReportIcon,
  Notifications as NotificationIcon,
  Security as SecurityIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  Build as MaintenanceIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  Campaign as CampaignIcon,
  ScheduleSend as ScheduleIcon
} from '@mui/icons-material';

const AdminQuickActionsWidget = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [notificationData, setNotificationData] = useState({
    title: '',
    message: '',
    type: 'info'
  });

  const quickActions = [
    {
      title: 'Crear Usuario',
      description: 'Agregar nuevo usuario al sistema',
      icon: <PersonAddIcon />,
      color: 'primary',
      action: () => handleAction('create_user')
    },
    {
      title: 'Nuevo Evento',
      description: 'Crear evento desde admin',
      icon: <EventIcon />,
      color: 'success',
      action: () => handleAction('create_event')
    },
    {
      title: 'Backup Sistema',
      description: 'Crear respaldo completo',
      icon: <BackupIcon />,
      color: 'warning',
      action: () => handleAction('backup')
    },
    {
      title: 'Configuración',
      description: 'Ajustes del sistema',
      icon: <SettingsIcon />,
      color: 'secondary',
      action: () => handleAction('settings')
    }
  ];

  const communicationActions = [
    {
      title: 'Notificación',
      description: 'Enviar a todos los usuarios',
      icon: <NotificationIcon />,
      color: 'info',
      action: () => handleOpenDialog('notification')
    },
    {
      title: 'Email Masivo',
      description: 'Campaña de email',
      icon: <EmailIcon />,
      color: 'primary',
      action: () => handleAction('mass_email')
    },
    {
      title: 'Anuncio',
      description: 'Publicar anuncio general',
      icon: <CampaignIcon />,
      color: 'warning',
      action: () => handleAction('announcement')
    }
  ];

  const systemActions = [
    {
      title: 'Exportar Datos',
      icon: <DownloadIcon />,
      tooltip: 'Exportar toda la información',
      action: () => handleAction('export')
    },
    {
      title: 'Importar Datos',
      icon: <UploadIcon />,
      tooltip: 'Importar datos masivos',
      action: () => handleAction('import')
    },
    {
      title: 'Generar Reporte',
      icon: <ReportIcon />,
      tooltip: 'Crear reporte completo',
      action: () => handleAction('report')
    },
    {
      title: 'Mantenimiento',
      icon: <MaintenanceIcon />,
      tooltip: 'Modo mantenimiento',
      action: () => handleAction('maintenance')
    },
    {
      title: 'Actualizar Cache',
      icon: <RefreshIcon />,
      tooltip: 'Limpiar y actualizar cache',
      action: () => handleAction('refresh_cache')
    },
    {
      title: 'Auditoría',
      icon: <SecurityIcon />,
      tooltip: 'Revisar logs de seguridad',
      action: () => handleAction('audit')
    }
  ];

  const handleAction = (actionType) => {
    console.log(`Ejecutando acción: ${actionType}`);
    
    switch (actionType) {
      case 'create_user':
        // Navegar a crear usuario
        break;
      case 'create_event':
        // Navegar a crear evento
        break;
      case 'backup':
        // Iniciar backup
        alert('Iniciando backup del sistema...');
        break;
      case 'settings':
        // Abrir configuración
        break;
      case 'mass_email':
        // Abrir editor de email masivo
        break;
      case 'announcement':
        // Crear anuncio
        break;
      case 'export':
        // Exportar datos
        alert('Generando archivo de exportación...');
        break;
      case 'import':
        // Importar datos
        break;
      case 'report':
        // Generar reporte
        alert('Generando reporte completo...');
        break;
      case 'maintenance':
        // Activar modo mantenimiento
        alert('Activando modo mantenimiento...');
        break;
      case 'refresh_cache':
        // Actualizar cache
        alert('Actualizando cache del sistema...');
        break;
      case 'audit':
        // Revisar auditoría
        break;
      default:
        break;
    }
  };

  const handleOpenDialog = (type) => {
    setDialogType(type);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogType('');
    setNotificationData({ title: '', message: '', type: 'info' });
  };

  const handleSendNotification = () => {
    console.log('Enviando notificación:', notificationData);
    alert(`Notificación "${notificationData.title}" enviada a todos los usuarios`);
    handleCloseDialog();
  };

  return (
    <>
      <Card sx={{ height: '100%' }}>
        <CardHeader
          title="⚡ Acciones Rápidas"
          subheader="Herramientas de administración"
          action={
            <Chip 
              label="Admin" 
              color="error" 
              size="small"
              icon={<AdminIcon />}
            />
          }
        />
        
        <CardContent>
          {/* Acciones principales */}
          <Typography variant="subtitle2" gutterBottom>
            🚀 Acciones Principales
          </Typography>
          
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {quickActions.map((action, index) => (
              <Grid item xs={6} key={index}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={action.icon}
                  color={action.color}
                  onClick={action.action}
                  sx={{
                    height: 80,
                    flexDirection: 'column',
                    gap: 0.5,
                    '& .MuiButton-startIcon': {
                      margin: 0
                    }
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">
                    {action.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" fontSize="0.65rem">
                    {action.description}
                  </Typography>
                </Button>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Acciones de comunicación */}
          <Typography variant="subtitle2" gutterBottom>
            📢 Comunicación
          </Typography>
          
          <Grid container spacing={1} sx={{ mb: 3 }}>
            {communicationActions.map((action, index) => (
              <Grid item xs={4} key={index}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={action.icon}
                  color={action.color}
                  onClick={action.action}
                  sx={{
                    height: 60,
                    flexDirection: 'column',
                    gap: 0.5,
                    fontSize: '0.7rem',
                    '& .MuiButton-startIcon': {
                      margin: 0
                    }
                  }}
                >
                  {action.title}
                </Button>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Acciones del sistema */}
          <Typography variant="subtitle2" gutterBottom>
            🔧 Sistema
          </Typography>
          
          <Grid container spacing={1}>
            {systemActions.map((action, index) => (
              <Grid item xs={4} key={index}>
                <Tooltip title={action.tooltip}>
                  <IconButton
                    onClick={action.action}
                    sx={{
                      width: '100%',
                      height: 50,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      flexDirection: 'column',
                      gap: 0.5
                    }}
                  >
                    {action.icon}
                    <Typography variant="caption" fontSize="0.6rem">
                      {action.title}
                    </Typography>
                  </IconButton>
                </Tooltip>
              </Grid>
            ))}
          </Grid>

          {/* Estadísticas rápidas */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              💡 Tip: Usa Ctrl+Alt+A para acceder rápidamente a las acciones de admin
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Dialog para notificaciones */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          📢 Enviar Notificación Global
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Esta notificación se enviará a todos los usuarios activos de la plataforma.
          </Alert>
          
          <TextField
            fullWidth
            label="Título de la notificación"
            value={notificationData.title}
            onChange={(e) => setNotificationData({...notificationData, title: e.target.value})}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Mensaje"
            value={notificationData.message}
            onChange={(e) => setNotificationData({...notificationData, message: e.target.value})}
            sx={{ mb: 2 }}
          />
          
          <TextField
            select
            fullWidth
            label="Tipo de notificación"
            value={notificationData.type}
            onChange={(e) => setNotificationData({...notificationData, type: e.target.value})}
            SelectProps={{
              native: true,
            }}
          >
            <option value="info">Información</option>
            <option value="success">Éxito</option>
            <option value="warning">Advertencia</option>
            <option value="error">Error</option>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSendNotification} 
            variant="contained"
            disabled={!notificationData.title || !notificationData.message}
          >
            Enviar Notificación
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AdminQuickActionsWidget;